/**
 * Admin · Image library (super-admin) — manage the two image lists the editor's product
 * profile picks from: TEXTURES (overlay materials) and CLEAR-CASE BACKGROUNDS. Each list
 * is fetched cache-first (useCachedResource) so it shows instantly on re-open and quietly
 * revalidates. Adding an asset uploads the bytes to a presigned R2/CDN target (pixels never
 * hit the API — mirrors the bakedSync producer + AppearanceTab logo upload), then persists
 * the hosted ref; deleting removes it. The server enforces super-admin + hosting-enabled.
 */
import { useRef, useState } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { InfoHint } from "../ui/Tooltip.js";
import { useServices } from "../app/context.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import { StaleNotice } from "./StaleNotice.js";
import type { ImageAsset, ImageAssetKind } from "@genz/platform";
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "../templates/timeout.js";
import { RowAction } from "../ui/RowAction.js";

/** Map a picked file's name/MIME to an allowed presign ext (png/jpg/jpeg/webp). */
const ALLOWED_EXT = new Set(["png", "jpg", "jpeg", "webp"]);
function extFor(file: File): string {
  const fromName = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (ALLOWED_EXT.has(fromName)) return fromName === "jpeg" ? "jpg" : fromName;
  // Fall back to the MIME subtype (image/png → png, image/jpeg → jpg).
  const sub = (file.type.split("/").pop() ?? "").toLowerCase();
  if (sub === "jpeg") return "jpg";
  return ALLOWED_EXT.has(sub) ? sub : "png";
}

const SECTIONS: ReadonlyArray<{ kind: ImageAssetKind; title: string; sub: string }> = [
  {
    kind: "texture",
    title: "Textures",
    sub: "Overlay materials the editor's product profile can apply (e.g. fabric, leather, glitter).",
  },
  {
    kind: "clear_background",
    title: "Clear-case backgrounds",
    sub: "Backgrounds shown through a clear case that the editor's product profile can pick from.",
  },
];

/** One image list (textures or clear-case backgrounds) + its "add" row. */
function ImageAssetSection({ kind, title, sub }: { kind: ImageAssetKind; title: string; sub: string }) {
  const { client } = useServices();
  const { data, error: loadError, stale, revalidate } =
    useCachedResource<ImageAsset[]>(`admin.imageAssets.${kind}`, () => client.listImageAssets(kind));
  const assets = data ?? [];

  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function add() {
    if (!name.trim() || !file || adding) return;
    setAdding(true);
    setError(null);
    try {
      const target = await client.presignImageAsset(kind, extFor(file));
      // A non-http(s) upload_url means LOCAL hosting — we can't PUT to it, and the old code
      // then STILL created the library row: a URL pointing at bytes that were never written
      // anywhere, which later flowed verbatim into template baked refs (texture_url) and
      // 404'd on every device. Refuse loudly instead; never create a row without a real upload.
      if (!/^https?:\/\//.test(target.upload_url)) {
        throw new Error("Hosting is local-only — library images can't be published for other devices. Configure R2 hosting (Settings → Hosting) first.");
      }
      const bytes = new Uint8Array(await file.arrayBuffer());
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT",
        headers: { ...(target.fields ?? {}) },
        body: bytes as BlobPart as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Library image upload");
      if (!res.ok) throw new Error(`upload PUT → ${res.status}`);
      if (!/^https?:\/\//.test(target.public_url)) {
        throw new Error("Hosting returned a non-durable public URL — check hosting_public_host (Settings → Hosting).");
      }
      await client.createImageAsset(kind, name.trim(), target.public_url);
      setName("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await revalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  }

  async function remove(asset: ImageAsset) {
    if (deletingId) return;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(`Delete “${asset.name}”?`)) return;
    setDeletingId(asset.id);
    setError(null);
    try {
      await client.deleteImageAsset(asset.id);
      await revalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  const canAdd = name.trim().length > 0 && file != null && !adding;

  return (
    <div className="admin-panel" data-testid={`image-asset-section-${kind}`}>
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          {/* No `{title}` here (fix round 1, L-4). Under rule 8 the accordion HEAD is this
              section's title, and repeating it 30px below said "Textures / Textures". The sub
              stays: it is the only line that says what the section is for. */}
          <p className="admin-panel__sub">{sub}</p>
        </div>
      </div>

      {(error || loadError) && (
        <p role="alert" data-testid={`image-asset-error-${kind}`}>{error ?? loadError}</p>
      )}
      <StaleNotice stale={stale} testid={`image-asset-stale-${kind}`} />

      {assets.length === 0 ? (
        <EmptyState
          icon="image"
          title={`No ${title.toLowerCase()} yet`}
          hint="Add one below — it becomes selectable in the editor's product profile."
          testId={`image-asset-empty-${kind}`}
        />
      ) : (
        <ul className="image-asset-grid" data-testid={`image-asset-list-${kind}`}>
          {assets.map((asset) => (
            <li key={asset.id} className="image-asset-card" data-testid="image-asset-row">
              <img className="image-asset-card__thumb" src={asset.image_url} alt={asset.name} loading="lazy" />
              <span className="image-asset-card__name" title={asset.name}>{asset.name}</span>
              <RowAction
                label="Delete"
                icon="trash"
                tone="danger"
                data-testid="image-asset-delete"
                disabled={deletingId === asset.id}
                busy={deletingId === asset.id}
                onClick={() => void remove(asset)}
              />
            </li>
          ))}
        </ul>
      )}

      <form
        className="image-asset-add"
        data-testid={`image-asset-add-${kind}`}
        onSubmit={(e) => { e.preventDefault(); void add(); }}
      >
        <div className="field">
          <label htmlFor={`image-asset-name-${kind}`}>Name</label>
          <input
            id={`image-asset-name-${kind}`}
            type="text"
            className="input"
            value={name}
            placeholder="e.g. Brushed metal"
            data-testid={`image-asset-name-${kind}`}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`image-asset-file-${kind}`}>Image (PNG / JPG / WebP)</label>
          <input
            id={`image-asset-file-${kind}`}
            type="file"
            accept="image/*"
            data-testid={`image-asset-file-${kind}`}
            ref={fileInputRef}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <ActionBar
          label={`${title} actions`}
          primary={
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canAdd}
              data-testid={`image-asset-add-submit-${kind}`}
            >
              {adding ? "Adding…" : "Add"}
            </button>
          }
        />
      </form>
    </div>
  );
}

export function ImageLibraryTab() {
  /* CONTROLLED (Z0 fix round F-6): the open section is owned here, so it survives every
     re-render the two upload forms cause instead of depending on a seed read once at mount. */
  const [open, setOpen] = useState<string | null>(SECTIONS[0]!.kind);
  return (
    <section className="admin-tab" data-testid="admin-image-library">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">Image library <InfoHint text="Manage the textures + clear-case backgrounds the editor's product profile picks from. Uploads host to the CDN; super-admin only." /></strong>
        </div>
      </div>
      {/* Rule 8. Two library sections, each with its own upload form and its own
          `.btn--primary` Add — one accordion, one section open, one Add on screen. A closed
          section's rows stay MOUNTED — React keeps them, so their state survives the fold —
          but they take no layout: `.accordion__item:not([open]) > .accordion__body` is
          `display: none`. Nothing is unreachable, because the rows the fold hides are one
          click on the section head away; nothing is CLICKABLE while folded either, which is
          why the design gate opens each section before it measures hit targets (G-26o). */}
      <Accordion
        name="admin-image-library"
        openKey={open}
        onOpenChange={setOpen}
        items={SECTIONS.map((s) => ({
          key: s.kind,
          head: s.title,
          body: <ImageAssetSection kind={s.kind} title={s.title} sub={s.sub} />,
        }))}
      />
    </section>
  );
}
